import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@televet/kibble-ui/Button';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { trpc } from '@/lib/trpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PageContainer } from '@/components/PageContainer';
import { DeleteDialog } from '@/components/DeleteDialog';
import { toaster } from '@/components/ui/toaster';

export const Route = createFileRoute('/_authenticated/llm-models/')({
  beforeLoad: () => {
    return {
      pageTitle: 'LLM Models',
    };
  },
  loader: () => {
    return {
      pageTitle: 'LLM Models',
    };
  },
  component: LlmModels,
});

function LlmModels() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [deleteModelId, setDeleteModelId] = useState<string | null>(null);

  const { data } = useQuery(trpc.llmModel.list.queryOptions());

  const { mutate: deleteModel, isPending: isDeleting } = useMutation(
    trpc.llmModel.deleteOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'LLM model deleted',
          description: data.message,
        });
        queryClient.invalidateQueries({
          queryKey: [['llmModel', 'list']],
        });
        setDeleteModelId(null);
      },
      onError: (error) => {
        const errorMessage = error.message || 'Failed to delete LLM model';
        toaster.error({
          title: 'Failed to delete LLM model',
          description: errorMessage,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const handleDelete = (modelId: string) => {
    setDeleteModelId(modelId);
  };

  const confirmDelete = () => {
    if (deleteModelId) {
      deleteModel({ id: deleteModelId });
    }
  };

  const filteredModels = data?.filter((model) =>
    searchValue
      ? model.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        model.externalModelId.toLowerCase().includes(searchValue.toLowerCase())
      : true
  );

  return (
    <PageContainer>
      <Flex direction="column" gap={4} p={8} width="100%">
        <Flex justify="space-between" align="center">
          <TextInput
            placeholder="Search by name or external model ID..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            maxW="300px"
          />
          <Button onClick={() => navigate({ to: '/llm-models/add' })}>
            Add LLM Model
          </Button>
        </Flex>
        <Table.Root size="lg" width="100%">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>External Model ID</Table.ColumnHeader>
              <Table.ColumnHeader>Provider</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filteredModels?.map((model) => (
              <Table.Row key={model.id}>
                <Table.Cell>{model.name}</Table.Cell>
                <Table.Cell>{model.externalModelId}</Table.Cell>
                <Table.Cell>{model.modelProvider}</Table.Cell>
                <Table.Cell textAlign="end">
                  <Flex gap={2} justify="flex-end">
                    <Button
                      iconName="pen"
                      variant="ghostNeutral"
                      size="sm"
                      onClick={() =>
                        navigate({ to: `/llm-models/${model.id}` })
                      }
                    />
                    <Button
                      iconName="trashcan"
                      variant="ghostDestructive"
                      size="sm"
                      onClick={() => handleDelete(model.id)}
                    />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        {filteredModels?.length === 0 && (
          <Flex justify="center" align="center" p={8}>
            No LLM models found
          </Flex>
        )}
      </Flex>
      <DeleteDialog
        isOpen={!!deleteModelId}
        title="Delete LLM Model"
        description={
          deleteModelId
            ? `Are you sure you want to delete this LLM model? This action cannot be undone.`
            : ''
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModelId(null)}
        isLoading={isDeleting}
        isDangerous
      />
    </PageContainer>
  );
}
